import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  CalendarDays, ChevronDown, Compass, Filter, Heart, Loader2, MapPin,
  Mountain, Search, SlidersHorizontal, Sparkles, Wallet,
  X,
} from "lucide-react";
import { toast } from "sonner";
import * as RangeSlider from "@radix-ui/react-slider";
import PaginationControls from "@/components/PaginationControls";
import { Btn, inputCls } from "@/components/Primitives";
import { ErrorState, LoadingState } from "@/components/States";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError, formatApiErrorDetail } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { discoveryCardTrip } from "@/lib/discoveryTrips";
import ParticipantProfileMenu from "@/components/participant/ParticipantProfileMenu";
import { travellerImage } from "@/lib/traveller";
import OrganizerFollowButton from "@/components/traveller/OrganizerFollowButton";

const PAGE_SIZE = 12;

const blankFilters = {
  search: "",
  type: "",
  date_from: "",
  date_to: "",
  duration_max: "",
  budget_min: "",
  budget_max: "",
  organization: "",
};

const QUICK_PROMPTS = ["Weekend trips", "Trips", "Treks", "Trips under 20000"];

const CARD_IMAGE_RULES = [
  {
    match: ["maldives", "male", "maafushi", "hulhumale", "atoll", "lagoon", "island", "snorkel", "scuba"],
    url: "https://images.unsplash.com/photo-1514282401047-d79a71a590e8?auto=format&fit=crop&w=1000&q=80",
  },
  {
    match: ["thailand", "phuket", "krabi", "phi phi", "koh", "bangkok"],
    url: "https://images.unsplash.com/photo-1519451241324-20b4ea2c4220?auto=format&fit=crop&w=1000&q=80",
  },
  {
    match: ["meghalaya", "north east", "northeast", "cherapunji", "cherrapunji", "mawsynram", "shillong", "forest"],
    url: "https://images.unsplash.com/photo-1441974231531-c6227db76b6e?auto=format&fit=crop&w=1000&q=80",
  },
  {
    match: ["spiti", "kaza", "tabo", "himachal"],
    url: "https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1000&q=80",
  },
  {
    match: ["hampta", "pass", "manali", "rohtang", "trek", "himalaya"],
    url: "https://images.unsplash.com/photo-1464822759023-fed622ff2c3b?auto=format&fit=crop&w=1000&q=80",
  },
  {
    match: ["goa", "coast", "beach", "sea", "konkan"],
    url: "https://images.unsplash.com/photo-1507525428034-b723cf961d3e?auto=format&fit=crop&w=1000&q=80",
  },
];
const DEFAULT_CARD_IMAGE = "https://images.unsplash.com/photo-1493246507139-91e8fad9978e?auto=format&fit=crop&w=1000&q=80";
const GENERATED_SHELL_TITLE_RE = /^(live|scoped|location|logistics)\s+[0-9a-f]{4,}$/i;
const KNOWN_SHELL_TITLES = new Set(["public ridge"]);

function cleanParams(params) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== "" && value !== null && value !== undefined && value !== false));
}

function money(value, currency = "INR") {
  if (value === null || value === undefined || value === "") return "Contact organizer for price";
  const number = Number(value);
  if (!Number.isFinite(number)) return String(value);
  return new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(number / 100);
}

function tripDateLabel(trip) {
  if (trip.start_date || trip.end_date) return [fmtDate(trip.start_date), fmtDate(trip.end_date)].filter(Boolean).join(" - ");
  return trip.season || trip.dates?.season || "Dates coming soon";
}

function coverUrl(item) {
  const trip = item.trip || item;
  const explicit = item.cover_image?.url || trip.cover_image?.url || item.cover_image || trip.cover_image || item.gallery?.find((m) => m.is_cover)?.url || "";
  if (explicit && !explicit.includes("photo-1493246507139-91e8fad9978e")) return explicit;
  const haystack = [trip.title, trip.region, trip.destination, trip.type, trip.summary, trip.description].filter(Boolean).join(" ").toLowerCase();
  return CARD_IMAGE_RULES.find((rule) => rule.match.some((keyword) => haystack.includes(keyword)))?.url || explicit || DEFAULT_CARD_IMAGE;
}

function orgName(item) {
  return item.organization_profile?.display_name || item.organization?.name || item.organization_name || "Organizer";
}

function tripNumber(value) {
  const number = Number(value || 0);
  return Number.isFinite(number) ? number : 0;
}

function looksLikeTripShell(item) {
  const trip = item.trip || item;
  return !String(trip.summary || "").trim()
    && !String(trip.description || "").trim()
    && !String(trip.destination || trip.region || "").trim()
    && !trip.start_date
    && !trip.end_date
    && !tripNumber(trip.base_price_minor ?? trip.price_minor ?? trip.price_per_pax_minor);
}

function isGeneratedPublicShell(item) {
  const trip = item.trip || item;
  const title = String(trip.title || "").trim().toLowerCase();
  return looksLikeTripShell(item) && (GENERATED_SHELL_TITLE_RE.test(title) || KNOWN_SHELL_TITLES.has(title));
}

function activeFilterChips(filters) {
  const chips = [];
  if (filters.search) chips.push({ key: "search", label: `Search: ${filters.search}` });
  if (filters.type) chips.push({ key: "type", label: filters.type === "trek" ? "Treks" : "Trips" });
  if (filters.date_from) chips.push({ key: "date_from", label: `${filters.date_to ? "From" : "On"} ${fmtDate(filters.date_from)}` });
  if (filters.date_to) chips.push({ key: "date_to", label: `${filters.date_from ? "Until" : "On"} ${fmtDate(filters.date_to)}` });
  if (filters.duration_max) chips.push({ key: "duration_max", label: `Up to ${filters.duration_max} days` });
  if (filters.budget_min != null && filters.budget_min !== "") chips.push({ key: "budget_min", label: `From INR ${filters.budget_min}` });
  if (filters.budget_max != null && filters.budget_max !== "") chips.push({ key: "budget_max", label: `Up to INR ${filters.budget_max}` });
  if (filters.organization) chips.push({ key: "organization", label: `Organizer: ${filters.organization}` });
  return chips;
}

function DiscoveryFilter({ name, value, open, onToggle, onClose, children }) {
  return <div className="discovery-filter-item">
    <button type="button" className={`discovery-filter-trigger${value ? " is-active" : ""}`} aria-label={`${name} filter`} aria-expanded={open} onClick={onToggle}>
      <span>{name}</span>{value && <strong>{value}</strong>}<ChevronDown size={14} />
    </button>
    <div className="discovery-filter-popup" hidden={!open}>
      <div className="discovery-popup-head"><strong>{name}</strong><button type="button" aria-label={`Close ${name} filter`} onClick={onClose}><X size={16} /></button></div>
      {children}
    </div>
  </div>;
}

function PublicTripSkeleton() {
  return (
    <div className="public-discovery-skeleton" aria-hidden="true">
      <span />
      <i />
      <b />
      <em />
    </div>
  );
}

function TripEnquiryButton({ trip }) {
  const dialogRef = useRef(null);
  const { user } = useAuth();
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const submit = async (event) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    setSending(true);
    setError("");
    try {
      await api.post(`/public/trips/${trip.id}/enquiries`, {
        name: String(form.get("name")).trim(),
        phone: String(form.get("phone")).trim(),
        pax: Number(form.get("pax")),
        email: String(form.get("email")).trim() || null,
        message: String(form.get("message")).trim(),
      });
      dialogRef.current.close();
      toast.success("Enquiry sent to the organizer");
    } catch (err) {
      setError(formatApiError(err, "Could not send enquiry. Please try again."));
    } finally {
      setSending(false);
    }
  };
  return <>
    <Btn type="button" className="w-full" onClick={() => { setError(""); dialogRef.current.showModal(); }}>Enquire now</Btn>
    <dialog ref={dialogRef} className="w-[calc(100%_-_2rem)] max-w-md rounded-2xl bg-[#FFFAF1] p-5 text-[#17211D] backdrop:bg-black/60" aria-labelledby={`enquiry-title-${trip.id}`}>
      <form onSubmit={submit} className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <h2 id={`enquiry-title-${trip.id}`} className="text-lg font-bold">Enquire about {trip.title}</h2>
          <button type="button" aria-label="Close enquiry" onClick={() => dialogRef.current.close()}><X className="h-5 w-5" /></button>
        </div>
        <label className="block text-sm">Name<input name="name" required maxLength={120} defaultValue={user?.full_name || ""} autoComplete="name" className={inputCls} /></label>
        <label className="block text-sm">Phone<input name="phone" type="tel" required minLength={6} maxLength={32} defaultValue={user?.phone || ""} autoComplete="tel" className={inputCls} /></label>
        <label className="block text-sm">Email (optional)<input name="email" type="email" defaultValue={user?.email || ""} autoComplete="email" className={inputCls} /></label>
        <label className="block text-sm">Number of slots<input name="pax" type="number" required min="1" step="1" placeholder="How many people?" className={inputCls} /></label>
        <label className="block text-sm">Message<textarea name="message" rows={4} maxLength={2000} defaultValue={`Hi, I'm interested in ${trip.title}${trip.start_date && trip.end_date ? ` (${tripDateLabel(trip)})` : ""}. Could you please share availability, what's included, and how to book? Thank you!`} className={inputCls} /><span className="mt-1 block text-xs text-[#718076]">Edit this message or add any questions for the organizer.</span></label>
        {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
        <Btn type="submit" disabled={sending} className="w-full">{sending ? "Sending..." : "Send enquiry"}</Btn>
      </form>
    </dialog>
  </>;
}

function PublicTripCard({ item, onWishlist, busy }) {
  const { user } = useAuth();
  const trip = item.trip || item;
  const image = coverUrl(item);
if (user?.account_type === "traveller") return <article className="traveller-explore-card"><Link className="traveller-explore-photo" to={`/discover/${trip.id}`} aria-label={`View ${trip.title}`}><img src={travellerImage(item)} alt={trip.destination || trip.region || trip.title} loading="lazy" /></Link><div className="traveller-explore-body"><div><Link to={`/discover/${trip.id}`}><h2>{trip.title}</h2></Link><button className="traveller-icon-button" disabled={busy} title="Save to wishlist" aria-label={`Add ${trip.title || "trip"} to wishlist`} onClick={() => onWishlist(trip)}>{busy ? <Loader2 size={18} className="animate-spin" /> : <Heart size={20} strokeWidth={1.7} />}</button></div><p>{trip.destination || trip.region || "Destination soon"}</p><div className="traveller-explore-card-meta"><span>{trip.duration_days ? `${trip.duration_days} days` : "Dates coming soon"}</span><span>{money(trip.display_price_minor, trip.currency)}</span></div><p className="text-xs">{tripDateLabel(trip)}</p>{trip.more_batches_count > 0 && <p className="text-xs font-semibold text-[#2D7A65]">+{trip.more_batches_count} more {trip.more_batches_count === 1 ? "batch" : "batches"} available</p>}<div className="traveller-explore-organizer"><span>{orgName(item)}</span><OrganizerFollowButton organizer={{ id: item.organization_id || item.organization_profile?.org_id, display_name: orgName(item) }} /></div><div className="mt-3"><TripEnquiryButton trip={trip} /></div></div></article>;
  return (
    <article className="public-discovery-trip-card group overflow-hidden rounded-[1.5rem] border border-white/70 bg-[#FFFAF1] shadow-[0_20px_50px_rgba(23,33,29,0.18)] transition duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(23,33,29,0.28)]">
      <button type="button" className="public-discovery-card-wishlist" onClick={() => onWishlist(trip)} disabled={busy} aria-label={`Add ${trip.title || "trip"} to wishlist`}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Heart className="h-4 w-4" aria-hidden="true" />}
      </button>
      <Link to={`/discover/${trip.id}`} className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#E5B85C]">
        <div className="relative h-[10.5rem] overflow-hidden">
          <img src={image} alt="" className="h-full w-full object-cover transition duration-500 group-hover:scale-105" loading="lazy" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#10231C]/95 via-[#10231C]/35 to-transparent" />
          <div className="absolute left-4 top-4 rounded-full bg-[#FFFAF1]/95 px-3 py-1 text-xs font-extrabold text-[#17211D] shadow-sm">
            {trip.duration_days || "?"} days
          </div>
          <div className="absolute bottom-4 left-4 right-4">
            <p className="mb-1 flex items-center gap-1 text-xs font-extrabold uppercase tracking-[0.18em] text-[#E5B85C]">
              <MapPin className="h-3.5 w-3.5" /> {trip.destination || trip.region || "Destination soon"}
            </p>
            <h2 className="font-[Plus_Jakarta_Sans,sans-serif] text-lg font-extrabold leading-tight text-white">{trip.title}</h2>
          </div>
        </div>
        <div className="space-y-2 px-4 py-3 text-xs text-[#516259]">
          <div className="flex items-start gap-2">
            <CalendarDays className="mt-0.5 h-3.5 w-3.5 shrink-0 text-[#2D7A65]" />
            <span>{tripDateLabel(trip)}</span>
          </div>
          {trip.more_batches_count > 0 && <p className="font-semibold text-[#2D7A65]">+{trip.more_batches_count} more {trip.more_batches_count === 1 ? "batch" : "batches"} available</p>}
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="inline-flex items-center gap-1 capitalize"><Mountain className="h-3.5 w-3.5 text-[#2D7A65]" />{trip.difficulty_grade || "Any level"}</span>
            <span className="font-bold text-[#17211D]">{money(trip.display_price_minor, trip.currency || "INR")}</span>
          </div>
          <p className="truncate text-[11px] text-[#7A847E]" title={orgName(item)}>By {orgName(item)}</p>
        </div>
      </Link>
      <div className="public-discovery-card-action border-t border-[#17211D]/10 p-3">
        <TripEnquiryButton trip={trip} />
      </div>
    </article>
  );
}

export default function PublicTripDiscovery() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => ({ ...blankFilters, search: searchParams.get("search") || searchParams.get("destination") || searchParams.get("activity") || "" }));
  const durationSliderMax = Math.max(60, (Number(filters.duration_max) || 0) + 1);
  const budgetSliderMax = Math.max(100000, Math.ceil(Math.max(Number(filters.budget_max) || 0, Number(filters.budget_min) || 0) / 500) * 500 + 500);
  const [trips, setTrips] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, page_size: PAGE_SIZE, total: 0, pages: 0 });
  const [error, setError] = useState("");
  const [busyTrip, setBusyTrip] = useState("");
  const [prompt, setPrompt] = useState(() => searchParams.get("search") || searchParams.get("destination") || searchParams.get("activity") || "");
  const [assistBusy, setAssistBusy] = useState(false);
  const [assistStatus, setAssistStatus] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [openFilter, setOpenFilter] = useState("");
  const filterBarRef = useRef(null);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia("(max-width: 767px)").matches);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState("");
  const bottomRef = useRef(null);
  const requestRef = useRef(0);
  const fetchingRef = useRef(false);
  const filtersRef = useRef(JSON.stringify(filters));

  useEffect(() => {
    const media = window.matchMedia("(max-width: 767px)");
    const change = () => {
      setIsMobile(media.matches);
      setPagination((prev) => ({ ...prev, page: 1 }));
    };
    media.addEventListener("change", change);
    return () => media.removeEventListener("change", change);
  }, []);

  const load = useCallback(async () => {
    const request = ++requestRef.current;
    const filterKey = JSON.stringify(filters);
    if (filtersRef.current !== filterKey) {
      filtersRef.current = filterKey;
      if (pagination.page !== 1) {
        setPagination((prev) => ({ ...prev, page: 1 }));
        return;
      }
    }
    const append = isMobile && pagination.page > 1;
    fetchingRef.current = true;
    setError("");
    setMoreError("");
    setLoadingMore(append);
    if (!append) setTrips(null);
    try {
      const { data } = await api.get("/public/trips", {
        params: cleanParams({
          search: filters.search,
          type: filters.type,
          date_from: filters.date_from,
          date_to: filters.date_to,
          duration_max: filters.duration_max,
          budget_min: filters.budget_min,
          budget_max: filters.budget_max,
          organization: filters.organization,
          page: pagination.page,
          page_size: pagination.page_size,
        }),
      });
      const rows = data.data || [];
      const enriched = await Promise.all(rows.map(async (trip) => {
        const [detail, reviews] = await Promise.allSettled([
          api.get(`/public/trips/${trip.id}`),
          api.get(`/public/trips/${trip.id}/reviews`),
        ]);
        if (detail.status === "rejected" && detail.reason?.response?.status === 404) return null;
        const cardTrip = discoveryCardTrip(trip, detail.status === "fulfilled" ? detail.value.data.trip : null);
        if (!cardTrip) return null;
        return {
          ...(detail.status === "fulfilled" ? detail.value.data : {}),
          trip: cardTrip,
          review_summary: reviews.status === "fulfilled" ? reviews.value.data.summary : null,
        };
      }));
      if (request !== requestRef.current) return;
      const nextTrips = enriched.filter(Boolean).filter((item) => !isGeneratedPublicShell(item));
      setTrips((prev) => {
        if (!append) return nextTrips;
        const combined = [...(prev || []), ...nextTrips];
        return [...new Map(combined.map((item) => [(item.trip || item).id, item])).values()];
      });
      setPagination((prev) => ({ ...prev, ...(data.pagination || {}) }));
    } catch (err) {
      if (request !== requestRef.current) return;
      const message = formatApiError(err, "Could not load public trips.");
      if (append) setMoreError(message);
      else setError(message);
    } finally {
      if (request === requestRef.current) {
        fetchingRef.current = false;
        setLoadingMore(false);
      }
    }
  }, [filters, pagination.page, pagination.page_size, isMobile]);

  useEffect(() => { load(); return () => { requestRef.current += 1; }; }, [load]);

  useEffect(() => {
    if (!isMobile || !bottomRef.current || trips === null || error || moreError || loadingMore || pagination.page >= pagination.pages) return;
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(([entry]) => {
      if (!entry.isIntersecting || fetchingRef.current) return;
      fetchingRef.current = true;
      setPagination((prev) => ({ ...prev, page: prev.page + 1 }));
    }, { rootMargin: "150px" });
    observer.observe(bottomRef.current);
    return () => observer.disconnect();
  }, [isMobile, trips, error, moreError, loadingMore, pagination.page, pagination.pages]);

  useEffect(() => {
    if (!openFilter) return;
    const outside = (event) => { if (!filterBarRef.current?.contains(event.target)) setOpenFilter(""); };
    const escape = (event) => { if (event.key === "Escape") setOpenFilter(""); };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => { document.removeEventListener("pointerdown", outside); document.removeEventListener("keydown", escape); };
  }, [openFilter]);

  const updateFilter = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const applyFilters = (changes) => {
    setFilters((prev) => ({ ...prev, ...changes }));
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const runAssist = async (value = prompt) => {
    const text = value.trim();
    if (!text) return;
    if (["trips", "treks"].includes(text.toLowerCase())) {
      applyFilters({ type: text.toLowerCase() === "treks" ? "trek" : "trip", search: "" });
      setPrompt(text);
      setAssistStatus("Search applied. Matching trips are shown below.");
      return;
    }
    setAssistBusy(true);
    setAssistStatus("");
    try {
      const { data } = await api.post("/public/trips/search-assist", { prompt: text });
      const interpreted = data.filters || {};
      const hasFilters = Object.values(interpreted).some((value) => value !== "" && value !== false && value != null);
      const supported = Object.fromEntries(Object.entries(interpreted).filter(([key]) => ["search", "type", "date", "date_from", "date_to", "duration", "duration_max", "budget", "budget_min", "budget_max", "organization"].includes(key)));
      const keywords = [interpreted.destination, interpreted.activity].filter(Boolean).join(" ");
      if (!supported.search && keywords) supported.search = keywords;
      if (!Object.keys(supported).length || !hasFilters) supported.search = text;
      if (supported.date) { supported.date_from = supported.date; delete supported.date; }
      if (supported.budget != null) { supported.budget_max = supported.budget; delete supported.budget; }
      if (supported.duration) { supported.duration_max = { "1-3": 3, "4-7": 7 }[supported.duration] || ""; delete supported.duration; }
      applyFilters({ ...blankFilters, ...supported });
      setPrompt(text);
      setAssistStatus("Search applied. Matching trips are shown below.");
    } catch (err) {
      applyFilters({ ...blankFilters, search: text });
      setPrompt(text);
      setAssistStatus("Searching trip titles, destinations, and descriptions.");
    } finally {
      setAssistBusy(false);
    }
  };

  const filteredTrips = useMemo(() => (trips || []).filter((item) => !isGeneratedPublicShell(item)), [trips]);
  const chips = activeFilterChips(filters);
  const resultCountLabel = trips === null ? "Finding trips" : `${pagination.total} ${pagination.total === 1 ? "trip" : "trips"}`;

  const addToWishlist = async (trip) => {
    if (!user) {
      nav(`/login?next=${encodeURIComponent(`${window.location.pathname}${window.location.search}`)}`);
      return;
    }
    setBusyTrip(trip.id);
    try {
      await api.post(user.account_type === "traveller" ? "/traveller/wishlist" : "/participants/me/wishlist", {
        public_trip_id: trip.id,
        destination: trip.destination || trip.region || "",
        title: trip.title,
        notes: "[type: public_trip]",
        priority: "medium",
        status: "saved",
        visibility: "private",
      });
      toast.success("Trip added to wishlist");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusyTrip("");
    }
  };

  return (
    <main className="public-discovery-page tp-app-shell min-h-screen p-4 text-[#1A2420] sm:p-6" data-testid="public-trip-discovery">
      <div className="mx-auto max-w-7xl">
        {user?.account_type === "traveller" ? <>
          <div className="traveller-page-heading traveller-explore-heading"><div><h1>Explore</h1></div></div>
          <form className="traveller-explore-search" onSubmit={(event) => { event.preventDefault(); runAssist(); }}><Search size={18} /><input aria-label="Describe the trip you want" placeholder="A place, a weekend, a new adventure..." value={prompt} onChange={(event) => { setPrompt(event.target.value); setAssistStatus("Press Enter or Find matches to apply this search."); }} /><button className="traveller-icon-button" type="submit" disabled={assistBusy} aria-label="Find matches" title="Find matches">{assistBusy ? <Loader2 size={18} className="animate-spin" /> : <Compass size={22} />}</button></form>
          {assistStatus && <p className="mb-4 text-xs text-gray-500">{assistStatus}</p>}
        </> : <section className="public-discovery-hero relative mb-4 overflow-hidden rounded-2xl border border-white/20 bg-[#10231C] p-4 shadow-xl sm:px-6 sm:py-4">
          <img src="https://images.unsplash.com/photo-1500534314209-a25ddb2bd429?auto=format&fit=crop&w=1800&q=80" alt="" className="absolute inset-0 h-full w-full object-cover opacity-55" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0A110D]/95 via-[#0A110D]/70 to-[#0A110D]/20" />
          <div className="relative">
            <div className="public-discovery-header mb-3 flex flex-wrap items-center justify-between gap-3">
              <Link to="/" className="inline-flex items-center gap-2 text-sm font-bold text-[#FFFAF1]"><Compass className="h-5 w-5 text-[#E5B85C]" /> TrailPulse</Link>
              <div className="public-discovery-header-actions flex gap-2">
                <Link to="/flight-deals" className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[#E5B85C] px-4 py-2 text-sm font-bold text-[#17211D] hover:bg-[#f0c96f]"><Wallet className="h-4 w-4" /> Flight deals</Link>
                <Link to={user?.account_type === "traveller" ? "/traveller/wishlist" : "/my/hub#wishlist"} className="inline-flex min-h-10 items-center gap-2 rounded-full bg-[#FFFAF1]/90 px-4 py-2 text-sm font-bold text-[#17211D] hover:bg-white"><Heart className="h-4 w-4" /> My wishlist</Link>
                {user ? <ParticipantProfileMenu /> : <Link to="/login" className="inline-flex min-h-10 items-center rounded-full border border-white/25 px-4 py-2 text-sm font-bold text-white hover:bg-white/10">Sign in</Link>}
              </div>
            </div>
            <p className="public-discovery-kicker mb-2 text-xs font-extrabold uppercase tracking-[0.26em] text-[#E5B85C]">Public discovery</p>
            <h1 className="max-w-3xl font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-extrabold leading-tight text-[#FFFAF1] sm:text-3xl">Find a trip that fits your mood.</h1>

            <form
              className="public-discovery-search mt-4 rounded-xl border border-white/20 bg-[#FFFAF1]/95 p-2 shadow-[0_18px_60px_rgba(0,0,0,0.28)] backdrop-blur"
              onSubmit={(event) => { event.preventDefault(); runAssist(); }}
            >
              <label htmlFor="discovery-ai-search" className="sr-only">Describe the trip you want</label>
              <div className="flex gap-2">
                <div className="relative min-w-0 flex-1">
                  <Sparkles className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-[#2D7A65]" />
                  <input
                    id="discovery-ai-search"
                    value={prompt}
                    onChange={(event) => { setPrompt(event.target.value); setAssistStatus("Press Enter or Find matches to apply this search."); }}
                    placeholder="Search a destination or try: Maldives, trips under 20000..."
                    className="min-h-10 w-full rounded-xl border border-[#D8D2C3] bg-white px-12 text-sm font-semibold text-[#17211D] outline-none transition focus:border-[#2D7A65] focus:ring-2 focus:ring-[#2D7A65]/20"
                  />
                </div>
                <Btn type="submit" disabled={assistBusy} className="min-h-10 shrink-0 px-3 sm:px-4">
                  {assistBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} <span className="sm:hidden">Find</span><span className="hidden sm:inline">Find matches</span>
                </Btn>
              </div>
              <div className="public-discovery-suggestions mt-2 flex flex-wrap gap-2">
                {QUICK_PROMPTS.map((item) => (
                  <button key={item} type="button" onClick={() => runAssist(item)} className="rounded-full border border-[#D8D2C3] bg-white px-3 py-1.5 text-xs font-bold text-[#516259] transition hover:border-[#2D7A65] hover:text-[#2D7A65]">
                    {item}
                  </button>
                ))}
              </div>
              {assistStatus && <p className="mt-3 text-xs font-semibold text-[#2D7A65]">{assistStatus}</p>}
            </form>
          </div>
        </section>}


        {showFilters && <section ref={filterBarRef} className="discovery-filter-toolbar" aria-label="Public trip filters">
          <span className="discovery-toolbar-label"><SlidersHorizontal size={15} /> Refine</span>
          <DiscoveryFilter name="Dates" value={filters.date_from || filters.date_to ? [filters.date_from, filters.date_to].filter(Boolean).map(fmtDate).join(" - ") : ""} open={openFilter === "dates"} onToggle={() => setOpenFilter((current) => current === "dates" ? "" : "dates")} onClose={() => setOpenFilter("")}>
              <div className="public-discovery-date-field">
                <div className="public-discovery-date-heading"><span>Travel dates</span></div>
                <div className="public-discovery-date-range">
                  <label>From<input aria-label="Travel from" type="date" max={filters.date_to || undefined} className={inputCls} value={filters.date_from} onChange={(e) => updateFilter("date_from", e.target.value)} /></label>
                  <label>To<input aria-label="Travel until" type="date" min={filters.date_from || undefined} className={inputCls} value={filters.date_to} onChange={(e) => updateFilter("date_to", e.target.value)} /></label>
                </div>
              </div>
          </DiscoveryFilter>
          <DiscoveryFilter name="Type" value={filters.type === "trek" ? "Treks" : filters.type === "trip" ? "Trips" : ""} open={openFilter === "type"} onToggle={() => setOpenFilter((current) => current === "type" ? "" : "type")} onClose={() => setOpenFilter("")}>
              <label className="discovery-type-field">Trip type
                <select aria-label="Trip type" className={inputCls} value={filters.type} onChange={(e) => updateFilter("type", e.target.value)}>
                  <option value="">All types</option>
                  <option value="trip">Trips</option>
                  <option value="trek">Treks</option>
                </select>
              </label>
          </DiscoveryFilter>
          <DiscoveryFilter name="Duration" value={filters.duration_max ? `Up to ${filters.duration_max} days` : ""} open={openFilter === "duration"} onToggle={() => setOpenFilter((current) => current === "duration" ? "" : "duration")} onClose={() => setOpenFilter("")}>
              <div className="public-discovery-slider-field discovery-duration-field">
                <div className="discovery-field-heading"><label htmlFor="discovery-duration">Duration</label><span>{filters.duration_max ? `Up to ${filters.duration_max} days` : "Any length"}</span></div>
                <input id="discovery-duration" aria-label="Maximum duration slider" aria-valuetext={filters.duration_max ? `Up to ${filters.duration_max} days` : "Any duration"} type="range" min="1" max={durationSliderMax} value={filters.duration_max || durationSliderMax} onChange={(e) => updateFilter("duration_max", Number(e.target.value) === durationSliderMax ? "" : e.target.value)} />
                <div className="discovery-duration-detail"><span>Up to</span><input aria-label="Maximum duration days" type="number" min="1" className={inputCls} value={filters.duration_max} onChange={(e) => updateFilter("duration_max", e.target.value)} placeholder="Any" /><span>days</span></div>
              </div>
          </DiscoveryFilter>
          <DiscoveryFilter name="Budget" value={filters.budget_min || filters.budget_max ? `${filters.budget_min || 0} - ${filters.budget_max || "Any"}` : ""} open={openFilter === "budget"} onToggle={() => setOpenFilter((current) => current === "budget" ? "" : "budget")} onClose={() => setOpenFilter("")}>
              <div className="public-discovery-slider-field discovery-budget-field">
                <div className="discovery-field-heading"><span>Budget (INR)</span><span>{filters.budget_max ? `Up to ${Number(filters.budget_max).toLocaleString("en-IN")}` : "Any"}</span></div>
                <RangeSlider.Root className="discovery-price-slider" min={0} max={budgetSliderMax} step={500}
                  value={[Number(filters.budget_min) || 0, filters.budget_max !== "" && filters.budget_max != null ? Number(filters.budget_max) : budgetSliderMax]}
                  onValueChange={([min, max]) => applyFilters({
                    budget_min: min !== (Number(filters.budget_min) || 0) || filters.budget_min !== "" && filters.budget_min != null ? String(min) : "",
                    budget_max: max === budgetSliderMax ? "" : String(max),
                  })}>
                  <RangeSlider.Track className="discovery-price-track"><RangeSlider.Range className="discovery-price-range" /></RangeSlider.Track>
                  <RangeSlider.Thumb className="discovery-price-thumb" aria-label="Minimum budget slider" />
                  <RangeSlider.Thumb className="discovery-price-thumb" aria-label="Maximum budget slider" aria-valuetext={filters.budget_max !== "" && filters.budget_max != null ? `INR ${filters.budget_max}` : "Any price"} />
                </RangeSlider.Root>
                <div className="public-discovery-budget-inputs">
                  <label>Minimum<input aria-label="Budget min" type="number" min="0" max={filters.budget_max || undefined} className={inputCls} value={filters.budget_min ?? ""} onChange={(e) => updateFilter("budget_min", e.target.value)} placeholder="No minimum" /></label>
                  <label>Maximum<input aria-label="Budget max" type="number" min={filters.budget_min || 0} className={inputCls} value={filters.budget_max ?? ""} onChange={(e) => updateFilter("budget_max", e.target.value)} placeholder="No maximum" /></label>
                </div>
              </div>
          </DiscoveryFilter>
          <DiscoveryFilter name="Organizer" value={filters.organization} open={openFilter === "organizer"} onToggle={() => setOpenFilter((current) => current === "organizer" ? "" : "organizer")} onClose={() => setOpenFilter("")}>
              <label className="discovery-organizer-field">Organizer
                <input aria-label="Organization" className={inputCls} value={filters.organization} onChange={(e) => updateFilter("organization", e.target.value)} placeholder="Organizer name" />
              </label>
          </DiscoveryFilter>
          <button type="button" className="discovery-toolbar-reset" onClick={() => { setFilters(blankFilters); setPrompt(""); setAssistStatus(""); setOpenFilter(""); }}>Reset</button>
          <button type="button" className="discovery-toolbar-close" aria-label="Close filters" onClick={() => { setShowFilters(false); setOpenFilter(""); }}><X size={16} /></button>
        </section>}

        <div className="public-discovery-result-bar mb-4 flex flex-wrap items-center justify-between gap-3 text-sm text-[#EAF5EF]">
          <div className="public-discovery-active-chips flex flex-wrap gap-2">
            <strong className="public-discovery-mobile-count">{resultCountLabel}</strong>
            {chips.length === 0 ? (
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 font-semibold backdrop-blur"><Filter className="h-4 w-4 text-[#E5B85C]" /> Showing all published trips</span>
            ) : chips.map((chip) => (
              <button key={chip.key} type="button" onClick={() => updateFilter(chip.key, typeof filters[chip.key] === "boolean" ? false : "")} className="rounded-full bg-[#FFFAF1] px-3 py-1.5 text-xs font-extrabold text-[#17211D] shadow-sm">
                {chip.label} x
              </button>
            ))}
          </div>
          <div className="public-discovery-result-actions">
            <button type="button" className="inline-flex min-h-10 items-center gap-2 rounded-full border border-white/20 bg-white/10 px-4 text-sm font-bold text-white backdrop-blur hover:bg-white/15" aria-expanded={showFilters} onClick={() => setShowFilters((open) => !open)}><SlidersHorizontal className="h-4 w-4" aria-hidden="true" /> Filters</button>
            <Btn className="public-discovery-clear-action" variant="secondary" onClick={() => { setFilters(blankFilters); setPrompt(""); setAssistStatus(""); }}><Search className="h-4 w-4" /> Clear all</Btn>
          </div>
        </div>

        {error ? <ErrorState message={error} onRetry={load} />
          : trips === null ? (
            <>
              <LoadingState label="Finding public trips..." />
              <div className="public-discovery-skeleton-grid">
                <PublicTripSkeleton />
                <PublicTripSkeleton />
              </div>
            </>
          )
          : filteredTrips.length === 0 ? (
            <>
              <div className="public-discovery-empty">
                <Mountain className="h-6 w-6" aria-hidden="true" />
                <h2>No exact matches.</h2>
                <p>Try broadening your search or explore all published trips.</p>
                <button type="button" onClick={() => { setFilters(blankFilters); setPrompt(""); setAssistStatus(""); }}>Clear filters</button>
              </div>
              {(trips || []).length ? (
                <div className="public-discovery-mobile-fallback">
                  <p>Available trips</p>
                  <div className="public-discovery-results-grid grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                    {(trips || []).map((item) => <PublicTripCard key={(item.trip || item).id} item={item} onWishlist={addToWishlist} busy={busyTrip === (item.trip || item).id} />)}
                  </div>
                </div>
              ) : null}
            </>
          )
          : (
            <div className="space-y-4">
              <div className="public-discovery-results-grid grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                {filteredTrips.map((item) => <PublicTripCard key={(item.trip || item).id} item={item} onWishlist={addToWishlist} busy={busyTrip === (item.trip || item).id} />)}
              </div>
              {!isMobile && <div className="public-discovery-pagination-bottom">
                <PaginationControls pagination={pagination} onPageChange={(page) => setPagination((p) => ({ ...p, page }))} />
              </div>}
            </div>
          )}
        {isMobile && trips !== null && !error && <div ref={bottomRef} className="flex min-h-16 items-center justify-center gap-2 p-4 text-sm" aria-live="polite">
          {loadingMore ? <><Loader2 className="h-5 w-5 animate-spin" /> Loading more trips...</> : moreError ? <div><p role="alert">{moreError}</p><Btn onClick={load}>Retry</Btn></div> : pagination.page < pagination.pages ? <Btn variant="secondary" onClick={() => { if (!fetchingRef.current) { fetchingRef.current = true; setPagination((prev) => ({ ...prev, page: prev.page + 1 })); } }}>Load more trips</Btn> : trips.length > 0 ? "You've seen all trips" : null}
        </div>}
      </div>
      {busyTrip && <span className="sr-only">Saving wishlist item</span>}
    </main>
  );
}
